import { useState, useEffect, useRef, type FC, type ReactNode } from "react";
import { Card, Modal, Tooltip } from "@uhg-harmony/react";
import { useAuth } from "react-oidc-context";
import { ButtonBar } from "../common/ButtonBar";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { templatesActions } from "../../../../redux/templates/templatesSlice";
import "../../styles/library.css";

import {
  CardHeader,
  BodyWrapper,
  Description,
  PreviewContainer,
  Thumb,
  ThumbIframe,
  ThumbIframeWrapper,
} from "../../styles/styles";

export interface TabContentProps {
  templateId?: string;
  heading: ReactNode;
  description: ReactNode;
  checked?: boolean;
  onClick?: () => void;
  /** Presigned URL (or local .html path) to fetch the template HTML from. */
  src?: string;
  onLoadTemplate?: (html: string) => void;
}

export const TabContent: FC<TabContentProps> = ({
  templateId,
  heading,
  description,
  checked,
  onClick,
  src,
  onLoadTemplate,
}) => {
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [openAfterLoad, setOpenAfterLoad] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Only dispatch API calls when the card is actually visible in the viewport.
  // This prevents 200 simultaneous presigned-URL requests when the Library opens.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Prefer templateId as cache key so the same template is never re-fetched
  // even if the presigned URL rotates on a new API call.
  const cacheKey = templateId ?? src ?? "";

  // If src is an s3:// path, resolve it to a presigned URL first
  const isS3Path = src?.startsWith("s3://") ?? false;
  const resolvedPresignedUrl = useAppSelector((state) =>
    isS3Path && src ? state.templates.presignedUrlByS3Path[src] ?? null : null,
  );
  const isResolvingPresignedUrl = useAppSelector((state) =>
    isS3Path && src ? Boolean(state.templates.presignedUrlLoadingByS3Path[src]) : false,
  );
  const presignedUrlError = useAppSelector((state) =>
    isS3Path && src ? state.templates.presignedUrlErrorByS3Path[src] ?? null : null,
  );

  // Effective src: use the resolved presigned URL for s3:// paths, otherwise use src directly
  const effectiveSrc = isS3Path ? resolvedPresignedUrl ?? undefined : src;

  const previewHtml = useAppSelector((state) =>
    cacheKey ? state.templates.previewHtmlByKey[cacheKey] ?? null : null
  );
  const isLoadingPreview = useAppSelector((state) =>
    cacheKey ? Boolean(state.templates.previewLoadingByKey[cacheKey]) : false
  );
  const previewError = useAppSelector((state) =>
    cacheKey ? state.templates.previewErrorByKey[cacheKey] ?? null : null
  );

  // Step 1: For s3:// paths, dispatch presigned URL resolution — only when visible
  useEffect(() => {
    if (!isVisible || !isS3Path || !src || !auth.user?.access_token || resolvedPresignedUrl) return;
    dispatch(
      templatesActions.getTemplatePresignedUrlRequested({
        s3Path: src,
        token: auth.user.access_token,
      }),
    );
  }, [dispatch, isVisible, isS3Path, src, auth.user?.access_token, resolvedPresignedUrl]);

  // Step 2 (or direct): Auto-load the template HTML once we have an effective src — only when visible
  useEffect(() => {
    if (!isVisible || !effectiveSrc || !cacheKey || previewHtml) return;
    dispatch(
      templatesActions.fetchTemplatePreviewRequested({
        cacheKey,
        src: effectiveSrc,
      })
    );
  }, [dispatch, isVisible, effectiveSrc, cacheKey, previewHtml]);

  useEffect(() => {
    if (!openAfterLoad || !previewHtml) return;
    setIsPreviewOpen(true);
    setOpenAfterLoad(false);
  }, [openAfterLoad, previewHtml]);

  // Hide the card entirely if the presigned URL or the template HTML fetch failed (e.g. 404)
  // Must be AFTER all hooks/useEffects to obey the Rules of Hooks.
  if (presignedUrlError || previewError) return null;

  const handlePreviewOpen = () => {
    if (!effectiveSrc && !isResolvingPresignedUrl) return;
    onClick?.();
    // If already fetched during this session, open immediately — no network call.
    if (previewHtml !== null) {
      setIsPreviewOpen(true);
      return;
    }

    // Wait for the auto-load useEffect to complete
    setOpenAfterLoad(true);
  };

  const handlePreviewOpenFromModal = () => {
    if (!previewHtml) return;
    setIsPreviewOpen(false);
    onLoadTemplate?.(previewHtml);
  };

  const handlePreviewClose = () => {
    setIsPreviewOpen(false);
    setOpenAfterLoad(false);
  };

  return (
    <div
      ref={cardRef}
      className="col template-card-col"
      onClick={handlePreviewOpen}
    >
      <Card
        className={
          checked
            ? "harmony-card--color harmony-card--selected"
            : "harmony-card--color"
        }
        header={
          <CardHeader>
            <span>{heading}</span>
          </CardHeader>
        }
        content={
          <div className="pt-base">
            <BodyWrapper className="harmony-card__body">
              <Description className="harmony-card__content">
                {description}
              </Description>
            </BodyWrapper>
          </div>
        }
        buttonContainer={
          <PreviewContainer>
            <Tooltip content="Click to preview" placement="top">
              <ThumbIframeWrapper
                onClick={(e) => {
                  e.stopPropagation();
                  if (src) handlePreviewOpen();
                }}
              >
                <Thumb>
                  {previewHtml ? (
                    // Use srcDoc so the HTML is injected directly — no network
                    // call and no browser download dialog.
                    <ThumbIframe srcDoc={previewHtml} />
                  ) : (
                    <span className="template-thumb-placeholder">
                      {isResolvingPresignedUrl ? "Resolving\u2026" : isLoadingPreview ? "Loading\u2026" : src ? "Click to preview" : "No template"}
                    </span>
                  )}
                </Thumb>
              </ThumbIframeWrapper>
            </Tooltip>

            {isPreviewOpen && previewHtml && (
              <Modal
                open={isPreviewOpen}
                title={`Preview - ${heading}`}
                onClose={handlePreviewClose}
              >
                <Modal.Body>
                  <div className="template-preview-modal-body">
                    <iframe
                      srcDoc={previewHtml}
                      title="Preview"
                      referrerPolicy="no-referrer"
                      className="template-preview-iframe"
                    />
                  </div>
                </Modal.Body>
                <Modal.Actions>
                  <ButtonBar
                    onOpen={handlePreviewOpenFromModal}
                    closePreviewButton={true}
                    onClosePreview={handlePreviewClose}
                  />
                </Modal.Actions>
              </Modal>
            )}
          </PreviewContainer>
        }
      />
    </div>
  );
};
